/**
 * Small building blocks shared by the ribbon pages (docs/Decisions.md D42),
 * styled only with the dcon class vocabulary in lib/ui.js.
 */
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { ui, cn } from '../../lib/ui';
import { BILLING_UPGRADE_PATH } from '../../lib/upgrade';

export function TextInput({ label, hint, className, ...props }) {
  const id = useId();
  return (
    <label className={cn(ui.field, className)}>
      <span className={ui.label}>{label}</span>
      <input className={ui.input} aria-describedby={hint ? `${id}-hint` : undefined} {...props} />
      {hint && (
        <span id={`${id}-hint`} className={ui.hint}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function SelectInput({ label, hint, className, children, ...props }) {
  const id = useId();
  return (
    <label className={cn(ui.field, className)}>
      <span className={ui.label}>{label}</span>
      <select className={ui.input} aria-describedby={hint ? `${id}-hint` : undefined} {...props}>
        {children}
      </select>
      {hint && (
        <span id={`${id}-hint`} className={ui.hint}>
          {hint}
        </span>
      )}
    </label>
  );
}

/**
 * docs/Decisions.md D51 item 4: members and org admins are told that designs
 * and names are reviewed and that the platform may revoke one.
 */
export const REVIEW_NOTICE =
  "Ribbon and badge designs and names are reviewed by the platform. A design may be revoked at the platform's discretion; a revoked design is no longer awarded or shown on any rack.";

/** The upgrade prompt a basic organization sees in place of a premium tool. */
export function PremiumNotice({ feature = 'ribbon-designer', isOrgAdmin = false }) {
  const what =
    feature === 'badge-visibility'
      ? 'Keeping a ribbon visible to one group or to your organization only'
      : 'Designing your own ribbons and badges, setting achievement levels and arranging racks';
  return (
    <div className={cn(ui.cardFlat, 'bg-dcon-accent-soft!')} role="note">
      <p className="mb-2">
        <span className={ui.pillPremium}>Premium</span>
      </p>
      <p className="mb-2 text-body-s text-dcon-ink-body">
        {what} is part of the premium plan. The pre-built platform ribbons stay available on every
        plan.
      </p>
      {isOrgAdmin ? (
        <Link className={cn(ui.btnAccent, ui.btnSm)} to={BILLING_UPGRADE_PATH}>
          Upgrade this organization
        </Link>
      ) : (
        <p className={cn(ui.muted, 'text-caption')}>
          Ask an organization admin to upgrade the plan.
        </p>
      )}
    </div>
  );
}

export function MoveButtons({ label, index, count, onMove }) {
  return (
    <span className="inline-flex gap-1">
      <button
        type="button"
        className={cn(ui.btnGhost, ui.btnSm)}
        onClick={() => onMove(index, -1)}
        disabled={index === 0}
        aria-label={`Move ${label} up`}
      >
        Up
      </button>
      <button
        type="button"
        className={cn(ui.btnGhost, ui.btnSm)}
        onClick={() => onMove(index, 1)}
        disabled={index === count - 1}
        aria-label={`Move ${label} down`}
      >
        Down
      </button>
    </span>
  );
}
